import { useCallback, useState } from 'react';
import { ChatBotV1 } from '@frontend/api-chat-bot';
import { useAppScopeStore } from '@frontend/scope';
import { ChatbotAssistantContextProps } from '../provider/chatbot-assistant.provider';
import { AIChatbotResponse } from '../types';

export const useChatbotAssistantConfig = (): ChatbotAssistantContextProps => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<AIChatbotResponse[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationIds[0];

  const { mutate: sendMessageMutate, isLoading: isSending } = ChatBotV1.Mutations.useAIAssistantChatMutation({
    options: {
      onSuccess: (response) => {
        addMessage({
          role: 'assistant',
          message: { content: response.content?.text?.join('\n') ?? '' },
          id: response.id || Date.now().toString(),
          timestamp: new Date().toISOString(),
          type: 'text',
        });
      },
      onError: (error) => {
        console.error('Error sending message:', error);
      },
    },
  });

  const addMessage = useCallback((chatbotResponse: AIChatbotResponse) => {
    setMessages((prev) => [...prev, chatbotResponse]);
  }, []);

  const sendMessage = useCallback(
    (message: string) => {
      addMessage({
        role: 'user',
        message: { content: message },
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        type: 'text',
      });

      sendMessageMutate({
        message,
        locationId,
      });
    },
    [addMessage, locationId, sendMessageMutate]
  );

  const toggleChatbotOpen = useCallback(() => {
    setIsOpen((state) => !state);
  }, []);

  const toggleMinimized = useCallback(() => {
    setIsMinimized((state) => !state);
  }, []);

  return {
    messages,
    isLoading,
    isSending,
    isMinimized,
    isOpen,
    position,
    addMessage,
    sendMessage,
    setIsLoading,
    toggleChatbotOpen,
    toggleMinimized,
    setPosition,
  };
};
